import { useToast } from '@cian/ui-kit/toast';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestAuthentication } from '../../actions/authentication/requestAuthentication';
import { NEWBUILDING_COMPARE_ENABLED_CONFIG } from '../../constants/newbuildingCompare';
import { NewbuildingComparisonButton } from '../../components/NewbuildingComparisonButton';
import { selectUser } from '../../selectors/user';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { useApplicationContext } from '../../utils/applicationContext';
import { CompareToastContent } from './CompareToastContent';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackDeleteNewbuildingFromComparison,
} from './tracking';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingComparisonButtonContainerProps {
  entityId: number;
  label: string;
}

export const NewbuildingComparisonButtonContainer: React.FC<INewbuildingComparisonButtonContainerProps> = ({
  entityId,
  label,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [isAdded, setIsAdded] = React.useState(false);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isNewbuildingCompareEnabled = config.get<boolean>(NEWBUILDING_COMPARE_ENABLED_CONFIG) || false;

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl}
            onClick={() => trackClickAddComparisonPopup(entityId)}
          />
        ),
        onClose: () => trackClickRemoveComparisonPopup(entityId),
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    const nextValue = !isAdded;

    if (!user.isAuthenticated) {
      dispatch(requestAuthentication());

      return;
    }

    setIsAdded(nextValue);

    try {
      const result = nextValue
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId: entityId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId: entityId });

      showComparisonToast(result);

      if (result.success === false) {
        setIsAdded(!nextValue);

        return;
      }

      if (nextValue) {
        trackAddNewbuildingToComparison({ entityId, label });
      } else {
        trackDeleteNewbuildingFromComparison({ entityId, label });
      }
    } catch {
      setIsAdded(!nextValue);
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [dispatch, entityId, httpApi, isAdded, label, logger, showComparisonToast, user.isAuthenticated]);

  if (!isNewbuildingCompareEnabled) {
    return null;
  }

  return <NewbuildingComparisonButton isAdded={isAdded} onClick={handleClick} />;
};
